import {
	BROWSER_VERIFY_OPERATION,
	resolveBrowserRequirement,
} from "@lobu/connector-sdk/browser-requirement";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useFormDraft } from "@/hooks/use-form-draft";
import { useMembers } from "@/hooks/use-members";
import type { ConnectionItem, ConnectorDefinitionItem } from "@/lib/api";
import {
	useAuthProfiles,
	useCreateAuthProfile,
	useCreateConnection,
	useDeleteAuthProfile,
	useDeleteConnection,
	useDevices,
	useExecuteAction,
	useInstallableConnectorCatalog,
	useInstallConnector,
	useReconnectAuthProfile,
	useUpdateConnection,
} from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";
import {
	buildEnvKeysSchema,
	buildInstallAuthSchemaForMethod,
	buildRequestedOAuthScopes,
	type EnvKeysMethod,
	getOptionalOAuthScopes,
	getSelectableMethods,
	getSelectedNoAuthMethod,
	type OAuthMethod,
} from "./auth-helpers";
import {
	cleanConnectorValues,
	createConnectorDraft,
} from "./dynamic-connector-form";
import { useOAuthAuthorization } from "./oauth-permissions";
import { useInstallableConnectorSelection } from "./use-installable-connector-selection";

const BROWSER_SETUP_HINT =
	"No paired Chrome browser is available. Open the Lobu Chrome extension, connect it to this workspace, and enable browser access.";

interface UseConnectionFormParams {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated?: (connectionId: number, connectorKey: string) => void;
	organizationId: string;
	editingConnection?: ConnectionItem | null;
	connections?: ConnectionItem[];
	initialConnectorKey?: string;
	/** Where "Back" goes when the connector was pre-selected via a deep link
	 *  (page route `/connectors/create/$connectorKey`). Without this, Back would
	 *  drop the user onto an empty connector picker at a URL that still names a
	 *  connector — a dead-end desync. Absent in the dialog flow, where Back
	 *  legitimately steps back to the picker. */
	onDeepLinkBack?: () => void;
}

export function useConnectionForm({
	open,
	onOpenChange,
	onCreated,
	organizationId,
	editingConnection,
	connections = [],
	initialConnectorKey,
	onDeepLinkBack,
}: UseConnectionFormParams) {
	const createConnection = useCreateConnection();
	const authorization = useOAuthAuthorization();
	const { session } = useAuthState();
	const updateConnection = useUpdateConnection();
	const createAuthProfile = useCreateAuthProfile();
	const deleteAuthProfileMutation = useDeleteAuthProfile();
	const reconnectAuthProfile = useReconnectAuthProfile();
	const {
		data: connectorCatalog = [],
		isLoading: isLoadingDefs,
		refetch: refetchConnectorCatalog,
	} = useInstallableConnectorCatalog(organizationId);
	const isEditMode = !!editingConnection;
	const deleteConnection = useDeleteConnection();
	const installConnectorMutation = useInstallConnector();

	// Confirmation states
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [confirmingDeleteProfileSlug, setConfirmingDeleteProfileSlug] =
		useState<string | null>(null);

	// Install custom connector state
	const [showInstallForm, setShowInstallForm] = useState(false);
	const [installSourceUrl, setInstallSourceUrl] = useState("");
	const [installError, setInstallError] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");

	// Selection state
	const [selectedConnector, setSelectedConnector] =
		useState<ConnectorDefinitionItem | null>(null);
	const [displayName, setDisplayName] = useState("");
	const [pendingBrowserConnectionId, setPendingBrowserConnectionId] = useState<
		number | null
	>(null);
	const verifyBrowser = useExecuteAction();
	const configDraft = useFormDraft(
		`${editingConnection?.id ?? "new"}:${selectedConnector?.key ?? ""}:${open}`,
		createConnectorDraft(
			selectedConnector?.options_schema,
			editingConnection
				? (editingConnection.config ?? {})
				: (selectedConnector?.default_connection_config ?? {}),
		),
		selectedConnector?.options_schema,
	);
	const { values: configValues, setValues: setConfigValues } = configDraft;
	const [selectedAuthMethodIndex, setSelectedAuthMethodIndex] =
		useState<number>(0);
	const [selectedAuthProfileSlug, setSelectedAuthProfileSlug] = useState<
		string | undefined
	>(undefined);
	const [selectedAppAuthProfileSlug, setSelectedAppAuthProfileSlug] = useState<
		string | undefined
	>(undefined);
	const [showNewRuntimeProfileForm, setShowNewRuntimeProfileForm] =
		useState(false);
	const [newRuntimeProfileName, setNewRuntimeProfileName] = useState("");
	const [newRuntimeProfileSlug, setNewRuntimeProfileSlug] = useState("");
	const [showNewAppProfileForm, setShowNewAppProfileForm] = useState(false);
	const [newAppProfileName, setNewAppProfileName] = useState("");
	const [newAppProfileSlug, setNewAppProfileSlug] = useState("");
	const [showNewOAuthAccountForm, setShowNewOAuthAccountForm] = useState(false);
	const [newOAuthAccountName, setNewOAuthAccountName] = useState("");
	const [newOAuthAccountSlug, setNewOAuthAccountSlug] = useState("");
	const [selectedOAuthOptionalScopes, setSelectedOAuthOptionalScopes] =
		useState<string[]>([]);
	const [showNewBrowserProfileForm, setShowNewBrowserProfileForm] =
		useState(false);
	const [newBrowserProfileName, setNewBrowserProfileName] = useState("");
	const [newBrowserProfileSlug, setNewBrowserProfileSlug] = useState("");
	const [selectedMemberUserId, setSelectedMemberUserId] = useState<
		string | undefined
	>(undefined);
	const [selectedDeviceWorkerId, setSelectedDeviceWorkerId] = useState<
		string | undefined
	>(editingConnection?.device_worker_id ?? undefined);
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [pendingAuthRunId, setPendingAuthRunId] = useState<number | null>(null);
	const [pendingAuthConnectionId, setPendingAuthConnectionId] = useState<
		number | null
	>(null);
	const { isAdmin } = useMembers(organizationId);
	const oauthProvider = useMemo(() => {
		const methods = getSelectableMethods(selectedConnector?.auth_schema);
		const oauth = methods.find((m) => m.type === "oauth") as
			| OAuthMethod
			| undefined;
		return oauth?.provider ?? undefined;
	}, [selectedConnector?.auth_schema]);

	const { data: authProfiles = [] } = useAuthProfiles(organizationId, {
		connectorKey: selectedConnector?.key,
		provider: oauthProvider,
	});

	// In edit mode, auto-select the connector and pre-fill form values
	useEffect(() => {
		if (!editingConnection || !open) return;
		if (selectedConnector) return;
		if (isLoadingDefs) return;

		const connector = connectorCatalog.find(
			(c) => c.key === editingConnection.connector_key,
		);

		const resolved: ConnectorDefinitionItem = connector ?? {
			key: editingConnection.connector_key,
			name: editingConnection.connector_name || editingConnection.connector_key,
			description: null,
			version: "",
			auth_schema: null,
			feeds_schema: null,
			actions_schema: null,
			options_schema: null,
			status: "active",
			login_enabled: false,
		};

		setSelectedConnector(resolved);
		setDisplayName(editingConnection.display_name ?? "");
		setSelectedAuthProfileSlug(
			editingConnection.auth_profile_slug ?? undefined,
		);
		setSelectedAppAuthProfileSlug(
			editingConnection.app_auth_profile_slug ?? undefined,
		);
		setSelectedDeviceWorkerId(editingConnection.device_worker_id ?? undefined);
		const authMethods = getSelectableMethods(resolved.auth_schema);
		const preferredMethodIndex = authMethods.findIndex((method) => {
			if (editingConnection.auth_profile_kind === "browser_session")
				return method.type === "browser";
			if (
				editingConnection.auth_profile_kind === "oauth_account" ||
				editingConnection.app_auth_profile_slug
			)
				return method.type === "oauth";
			if (editingConnection.auth_profile_kind === "env")
				return method.type === "env_keys";
			if (getSelectedNoAuthMethod(editingConnection, resolved.auth_schema))
				return method.type === "none";
			return false;
		});
		setSelectedAuthMethodIndex(
			preferredMethodIndex >= 0 ? preferredMethodIndex : 0,
		);
	}, [
		editingConnection,
		open,
		connectorCatalog,
		selectedConnector,
		isLoadingDefs,
	]);

	// --- Auth for selected connector ---
	const selectableMethods = useMemo(
		() => getSelectableMethods(selectedConnector?.auth_schema),
		[selectedConnector],
	);
	const activeMethod = selectableMethods[selectedAuthMethodIndex] ?? null;
	const runtimeEnvProfiles = useMemo(
		() => authProfiles.filter((profile) => profile.profile_kind === "env"),
		[authProfiles],
	);
	const runtimeOAuthProfiles = useMemo(() => {
		if (!activeMethod || activeMethod.type !== "oauth") return [];
		const provider = activeMethod.provider.toLowerCase();
		return authProfiles.filter(
			(profile) =>
				profile.profile_kind === "oauth_account" &&
				profile.provider?.toLowerCase() === provider &&
				// Members pick from their own accounts only; admins triage all of them.
				(isAdmin || profile.created_by === session?.user?.id),
		);
	}, [activeMethod, authProfiles, isAdmin, session?.user?.id]);
	const appOAuthProfiles = useMemo(() => {
		if (!activeMethod || activeMethod.type !== "oauth") return [];
		const provider = activeMethod.provider.toLowerCase();
		return authProfiles.filter(
			(profile) =>
				profile.profile_kind === "oauth_app" &&
				profile.status === "active" &&
				profile.provider?.toLowerCase() === provider,
		);
	}, [activeMethod, authProfiles]);
	const browserProfiles = useMemo(
		() =>
			authProfiles.filter(
				(profile) => profile.profile_kind === "browser_session",
			),
		[authProfiles],
	);
	const runtimeProfileSchema = useMemo(() => {
		if (!activeMethod || activeMethod.type !== "env_keys") return undefined;
		return buildEnvKeysSchema(activeMethod as EnvKeysMethod);
	}, [activeMethod]);
	const appProfileSchema = useMemo(
		() => buildInstallAuthSchemaForMethod(activeMethod),
		[activeMethod],
	);
	const runtimeDraft = useFormDraft(
		`${selectedConnector?.key}:${selectedAuthMethodIndex}:${showNewRuntimeProfileForm}`,
		createConnectorDraft(runtimeProfileSchema),
		runtimeProfileSchema,
	);
	const appDraft = useFormDraft(
		`${selectedConnector?.key}:${selectedAuthMethodIndex}:${showNewAppProfileForm}`,
		createConnectorDraft(appProfileSchema),
		appProfileSchema,
	);
	const {
		values: newRuntimeProfileValues,
		setValues: setNewRuntimeProfileValues,
	} = runtimeDraft;
	const { values: newAppProfileValues, setValues: setNewAppProfileValues } =
		appDraft;
	const availableOAuthOptionalScopes = useMemo(
		() =>
			activeMethod?.type === "oauth"
				? getOptionalOAuthScopes(activeMethod)
				: [],
		[activeMethod],
	);

	// Auto-select profiles in create mode when they become available
	useEffect(() => {
		if (isEditMode || !selectedConnector || !activeMethod) return;

		if (activeMethod.type === "oauth") {
			if (!selectedAppAuthProfileSlug && appOAuthProfiles.length > 0) {
				// Prefer the org-default app profile if an admin pinned one for this
				// connector. Multiple apps without a default need an explicit choice.
				const defaultProfile = appOAuthProfiles.find(
					(p) =>
						p.is_default_for_connector &&
						p.connector_key === selectedConnector.key,
				);
				const app =
					defaultProfile ??
					(isAdmin && appOAuthProfiles.length === 1
						? appOAuthProfiles[0]
						: undefined);
				if (app) setSelectedAppAuthProfileSlug(app.slug);
			}
			if (!selectedAuthProfileSlug && runtimeOAuthProfiles.length > 0) {
				setSelectedAuthProfileSlug(runtimeOAuthProfiles[0].slug);
			}
		} else if (activeMethod.type === "env_keys") {
			if (!selectedAuthProfileSlug && runtimeEnvProfiles.length > 0) {
				setSelectedAuthProfileSlug(runtimeEnvProfiles[0].slug);
			}
		} else if (
			activeMethod.type === "browser" &&
			activeMethod.mode !== "live"
		) {
			if (!selectedAuthProfileSlug && browserProfiles.length > 0) {
				setSelectedAuthProfileSlug(browserProfiles[0].slug);
			}
		}
	}, [
		isAdmin,
		isEditMode,
		selectedConnector,
		activeMethod,
		selectedAppAuthProfileSlug,
		selectedAuthProfileSlug,
		appOAuthProfiles,
		runtimeOAuthProfiles,
		runtimeEnvProfiles,
		browserProfiles,
	]);

	useEffect(() => {
		if (
			activeMethod?.type !== "oauth" ||
			availableOAuthOptionalScopes.length === 0
		) {
			setSelectedOAuthOptionalScopes((prev) => (prev.length === 0 ? prev : []));
			return;
		}

		const selectedProfile = runtimeOAuthProfiles.find(
			(p) => p.slug === selectedAuthProfileSlug,
		);
		const requestedOptionalScopes = (
			selectedProfile?.requested_scopes ?? []
		).filter((scope) => availableOAuthOptionalScopes.includes(scope));
		setSelectedOAuthOptionalScopes((prev) => {
			if (
				prev.length === requestedOptionalScopes.length &&
				prev.every((s, i) => s === requestedOptionalScopes[i])
			) {
				return prev;
			}
			return requestedOptionalScopes;
		});
	}, [
		activeMethod,
		availableOAuthOptionalScopes,
		runtimeOAuthProfiles,
		selectedAuthProfileSlug,
	]);

	// --- Handlers ---

	const resetProfileForms = useCallback(() => {
		setShowNewRuntimeProfileForm(false);
		setNewRuntimeProfileName("");
		setNewRuntimeProfileSlug("");
		setShowNewAppProfileForm(false);
		setNewAppProfileName("");
		setNewAppProfileSlug("");
		setShowNewOAuthAccountForm(false);
		setNewOAuthAccountName("");
		setNewOAuthAccountSlug("");
		setSelectedOAuthOptionalScopes([]);
		setShowNewBrowserProfileForm(false);
		setNewBrowserProfileName("");
		setNewBrowserProfileSlug("");
	}, []);

	const resetConnectorConfiguration = useCallback(() => {
		setSelectedAuthMethodIndex(0);
		setSelectedAuthProfileSlug(undefined);
		setSelectedAppAuthProfileSlug(undefined);
		resetProfileForms();
	}, [resetProfileForms]);

	// Device-worker binding ("Run on"). Device-only connectors (required_capability)
	// must be pinned to a device that advertises the capability; any other connector
	// may optionally be pinned. Only the caller's own devices that are attached to
	// this workspace are bind-able.
	const {
		data: devices = [],
		refetch: refetchDevices,
		isFetching: devicesFetching,
	} = useDevices();
	const browserRequired = !!resolveBrowserRequirement(
		selectedConnector?.browser,
		activeMethod?.type ?? "none",
	);
	const liveBrowser =
		activeMethod?.type === "browser" && activeMethod.mode === "live";
	const requiredCapability = browserRequired
		? "browser.debugger"
		: (selectedConnector?.required_capability ?? null);
	const deviceOptions = useMemo(() => {
		const base = devices.filter(
			(d) =>
				d.organization_id === organizationId &&
				(!browserRequired || d.platform === "chrome-extension") &&
				(!requiredCapability || d.capabilities.includes(requiredCapability)),
		);
		// In edit mode, make sure the connection's current device is selectable even
		// if /api/me/devices no longer returns it (e.g. it was moved or dropped off)
		// — otherwise the Select renders blank.
		const currentId = editingConnection?.device_worker_id;
		if (currentId && !base.some((d) => d.id === currentId)) {
			return [
				{
					id: currentId,
					worker_id: editingConnection?.device_worker_handle ?? currentId,
					platform: editingConnection?.device_platform ?? null,
					app_version: null,
					capabilities: [] as string[],
					label: editingConnection?.device_label ?? null,
					last_seen_at: "",
					online: editingConnection?.device_online ?? false,
					organization_id: organizationId,
					organization_name: null,
					organization_slug: null,
					connector_count: 0,
					connector_error_count: 0,
					last_sync_at: null,
					connectors: [],
					update_required: [],
				},
				...base,
			];
		}
		return base;
	}, [
		devices,
		requiredCapability,
		browserRequired,
		editingConnection,
		organizationId,
	]);
	// Reset the picked device whenever the connector changes (create mode).
	// biome-ignore lint/correctness/useExhaustiveDependencies: A connector change invalidates the picked device.
	useEffect(() => {
		if (isEditMode) return;
		setSelectedDeviceWorkerId(undefined);
	}, [selectedConnector?.key, isEditMode]);
	// Drop a stale selection (device removed / lost the capability), and auto-pick
	// when a device-only connector has exactly one eligible device.
	useEffect(() => {
		if (
			selectedDeviceWorkerId &&
			!deviceOptions.some((d) => d.id === selectedDeviceWorkerId) &&
			selectedDeviceWorkerId !== editingConnection?.device_worker_id
		) {
			setSelectedDeviceWorkerId(undefined);
			return;
		}
		if (isEditMode || !requiredCapability || selectedDeviceWorkerId) return;
		if (deviceOptions.length === 1)
			setSelectedDeviceWorkerId(deviceOptions[0].id);
	}, [
		isEditMode,
		requiredCapability,
		selectedDeviceWorkerId,
		deviceOptions,
		editingConnection,
	]);

	// When a device-bound browser auth profile is selected, force the connection
	// onto that profile's device. The profile lives on a specific Mac and cookies
	// never leave it — runs must execute there.
	const selectedBrowserProfile = useMemo(
		() =>
			selectedAuthProfileSlug
				? authProfiles.find(
						(p) =>
							p.slug === selectedAuthProfileSlug &&
							p.profile_kind === "browser_session",
					)
				: undefined,
		[authProfiles, selectedAuthProfileSlug],
	);
	const lockedDeviceWorkerIdFromProfile =
		selectedBrowserProfile?.device_worker_id ?? null;
	useEffect(() => {
		if (!lockedDeviceWorkerIdFromProfile) return;
		if (selectedDeviceWorkerId !== lockedDeviceWorkerIdFromProfile) {
			setSelectedDeviceWorkerId(lockedDeviceWorkerIdFromProfile);
		}
	}, [lockedDeviceWorkerIdFromProfile, selectedDeviceWorkerId]);

	const reloadConnectorCatalog = useCallback(async () => {
		const refreshed = await refetchConnectorCatalog();
		return refreshed.data ?? [];
	}, [refetchConnectorCatalog]);

	const {
		installingConnectorKey,
		setInstallingConnectorKey,
		previewConnector,
		handleConnectorSelect,
		handleInstallPreviewedConnector,
		clearPreview,
	} = useInstallableConnectorSelection({
		isInstalling: installConnectorMutation.isPending,
		installConnectorFromSourceUri: async (sourceUri) => {
			await installConnectorMutation.mutateAsync({ source_uri: sourceUri });
		},
		reloadConnectorCatalog,
		resetConnectorConfiguration,
		selectConnector: setSelectedConnector,
		clearSaveError: () => setSaveError(null),
		setInstallError,
	});

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedConnector(null);
			setPendingBrowserConnectionId(null);
			setDisplayName("");
			resetConnectorConfiguration();
			setSelectedMemberUserId(undefined);
			setSaveError(null);
			setShowDeleteConfirm(false);
			setConfirmingDeleteProfileSlug(null);
			setShowInstallForm(false);
			setInstallSourceUrl("");
			setInstallError(null);
			setInstallingConnectorKey(null);
			setSearchQuery("");
			setPendingAuthRunId(null);
			setPendingAuthConnectionId(null);
			clearPreview();
		}
		onOpenChange(newOpen);
	};

	const handleAuthClose = (completed = false) => {
		setPendingAuthRunId(null);
		if (isEditMode) return;
		if (
			completed &&
			pendingAuthConnectionId &&
			selectedConnector &&
			onCreated
		) {
			onCreated(pendingAuthConnectionId, selectedConnector.key);
		} else {
			handleOpenChange(false);
		}
	};

	const handleBack = () => {
		// Deep-linked create flow: the connector was fixed by the URL, so there is
		// no picker at this URL. The parent navigates to the catalog route instead
		// of showing a picker at a URL that still names the previous connector.
		if (selectedConnector && initialConnectorKey && onDeepLinkBack) {
			onDeepLinkBack();
			return;
		}
		if (selectedConnector) {
			setSelectedConnector(null);
			setPendingBrowserConnectionId(null);
			resetConnectorConfiguration();
			setSelectedMemberUserId(undefined);
			setSaveError(null);
			setInstallError(null);
		} else if (previewConnector) {
			clearPreview();
		}
	};

	// Auto-select connector by key (for deep links like ?connector=x)
	const autoSelectedConnectorRef = useRef(false);
	useEffect(() => {
		if (!open) {
			autoSelectedConnectorRef.current = false;
			return;
		}
		if (
			autoSelectedConnectorRef.current ||
			isEditMode ||
			!initialConnectorKey ||
			selectedConnector
		)
			return;
		const connector = connectorCatalog.find(
			(c) => c.key === initialConnectorKey,
		);
		if (connector) {
			autoSelectedConnectorRef.current = true;
			void handleConnectorSelect(connector);
		}
	}, [
		open,
		isEditMode,
		initialConnectorKey,
		connectorCatalog,
		selectedConnector,
		handleConnectorSelect,
	]);

	// Compute whether options_schema has unique (non-feed-config) fields
	const hasUniqueOptionsFields = useMemo(() => {
		if (!selectedConnector || !configDraft.revision) return false;

		// The options form collects manual credentials (e.g. Slack's bot token +
		// signing secret). Those belong ONLY to the manual/BYO auth path
		// (`type: "none"`). When a managed method (app install, OAuth) is active the
		// credentials are granted server-side by the handshake, so showing "paste
		// your bot token" here is misleading and contradicts the managed install.
		// Connectors whose only method is `none` (discord, telegram, …) still show
		// it, since that IS their auth path.
		if (activeMethod && activeMethod.type !== "none") return false;

		const optionsProps = (
			configDraft.revision as {
				properties?: Record<string, unknown>;
			}
		).properties;
		if (!optionsProps) return false;

		const feedConfigProps = new Set<string>();
		const feedsSchema = selectedConnector.feeds_schema as Record<
			string,
			{ configSchema?: { properties?: Record<string, unknown> } }
		> | null;
		if (feedsSchema) {
			for (const feedDef of Object.values(feedsSchema)) {
				if (feedDef.configSchema?.properties) {
					for (const propName of Object.keys(feedDef.configSchema.properties)) {
						feedConfigProps.add(propName);
					}
				}
			}
		}

		return Object.keys(optionsProps).some((key) => !feedConfigProps.has(key));
	}, [selectedConnector, activeMethod, configDraft.revision]);

	const isNoAuthConnector = useMemo(() => {
		const methods = getSelectableMethods(selectedConnector?.auth_schema);
		const allMethods =
			(selectedConnector?.auth_schema as { methods?: Array<{ type: string }> })
				?.methods ?? [];
		return (
			methods.length === 0 &&
			allMethods.length > 0 &&
			allMethods.every((m) => m.type === "none")
		);
	}, [selectedConnector]);

	const existingNoAuthConnection = useMemo(() => {
		if (!isNoAuthConnector || !selectedConnector || !selectedMemberUserId)
			return null;
		return (
			connections.find(
				(c) =>
					c.connector_key === selectedConnector.key &&
					c.created_by === selectedMemberUserId,
			) ?? null
		);
	}, [isNoAuthConnector, selectedConnector, selectedMemberUserId, connections]);

	const normalizeStringValues = useCallback(
		(values: Record<string, unknown>) => {
			return Object.fromEntries(
				Object.entries(values)
					.filter(
						([, value]) => typeof value === "string" && value.trim().length > 0,
					)
					.map(([key, value]) => [key, String(value).trim()]),
			);
		},
		[],
	);

	const navigateOAuthPopup = (connectUrl: string) => {
		void authorization.begin(async () => ({ connect_url: connectUrl }));
	};

	const handleCreateProfile = useCallback(
		async (params: {
			profileKind: "env" | "oauth_app" | "oauth_account" | "browser_session";
			displayName: string;
			slug: string;
			credentials?: Record<string, string>;
			authData?: Record<string, unknown>;
			requestedScopes?: string[];
			onSuccess: (result: {
				auth_profile?: { slug: string };
				pending_slug?: string;
				connect_url?: string;
			}) => void | Promise<void>;
		}) => {
			if (!selectedConnector || !activeMethod) return;
			try {
				const result = await createAuthProfile.mutateAsync({
					connector_key: selectedConnector.key,
					profile_kind: params.profileKind,
					display_name: params.displayName,
					slug: params.slug || undefined,
					...(params.credentials ? { credentials: params.credentials } : {}),
					...(params.authData ? { auth_data: params.authData } : {}),
					...(params.requestedScopes
						? { requested_scopes: params.requestedScopes }
						: {}),
				});
				await params.onSuccess(result);
			} catch (error) {
				setSaveError(
					error instanceof Error
						? error.message
						: "Failed to create auth profile",
				);
			}
		},
		[activeMethod, createAuthProfile, selectedConnector],
	);

	const handleCreateRuntimeProfile = useCallback(async () => {
		if (!runtimeDraft.canSubmit) return;
		if (!activeMethod || activeMethod.type !== "env_keys") return;
		const credentials = normalizeStringValues(newRuntimeProfileValues);
		await handleCreateProfile({
			profileKind: "env",
			displayName:
				newRuntimeProfileName.trim() || `${selectedConnector?.name} Auth`,
			slug: newRuntimeProfileSlug.trim(),
			credentials,
			onSuccess: (result) => {
				setSelectedAuthProfileSlug(result.auth_profile!.slug);
				setShowNewRuntimeProfileForm(false);
				setNewRuntimeProfileName("");
				setNewRuntimeProfileSlug("");
			},
		});
	}, [
		activeMethod,
		handleCreateProfile,
		newRuntimeProfileName,
		newRuntimeProfileSlug,
		newRuntimeProfileValues,
		runtimeDraft.canSubmit,
		normalizeStringValues,
		selectedConnector?.name,
	]);

	const handleCreateAppProfile = useCallback(async () => {
		if (!appDraft.canSubmit) return;
		if (!activeMethod || activeMethod.type !== "oauth") return;
		const credentials = normalizeStringValues(newAppProfileValues);
		await handleCreateProfile({
			profileKind: "oauth_app",
			displayName:
				newAppProfileName.trim() ||
				`${selectedConnector?.name} ${activeMethod.provider} App`,
			slug: newAppProfileSlug.trim(),
			credentials,
			onSuccess: (result) => {
				setSelectedAppAuthProfileSlug(result.auth_profile!.slug);
				setShowNewAppProfileForm(false);
				setNewAppProfileName("");
				setNewAppProfileSlug("");
			},
		});
	}, [
		activeMethod,
		handleCreateProfile,
		newAppProfileName,
		newAppProfileSlug,
		newAppProfileValues,
		appDraft.canSubmit,
		normalizeStringValues,
		selectedConnector?.name,
	]);

	const handleCreateOAuthAccountProfile = async () => {
		if (!selectedConnector || activeMethod?.type !== "oauth") return;
		if (!selectedAppAuthProfileSlug) {
			setSaveError("Choose an OAuth app before connecting your account.");
			return;
		}
		setSaveError(null);
		await authorization.begin(async () => {
			const result = await createAuthProfile.mutateAsync({
				connector_key: selectedConnector.key,
				profile_kind: "oauth_account",
				display_name:
					newOAuthAccountName.trim() ||
					`${selectedConnector.name} ${activeMethod.provider} Account`,
				slug: newOAuthAccountSlug.trim() || undefined,
				app_auth_profile_slug: selectedAppAuthProfileSlug,
				requested_scopes: buildRequestedOAuthScopes(
					activeMethod,
					selectedOAuthOptionalScopes,
				),
			});
			const slug = result.auth_profile?.slug ?? result.pending_slug;
			if (slug) setSelectedAuthProfileSlug(slug);
			setShowNewOAuthAccountForm(false);
			setNewOAuthAccountName("");
			setNewOAuthAccountSlug("");
			return { ...result, status: result.auth_profile?.status };
		});
	};

	const handleCreateBrowserProfile = useCallback(async () => {
		if (!activeMethod || activeMethod.type !== "browser") return;
		await handleCreateProfile({
			profileKind: "browser_session",
			displayName:
				newBrowserProfileName.trim() ||
				`${selectedConnector?.name || "Connector"} Browser Session`,
			slug: newBrowserProfileSlug.trim(),
			onSuccess: (result) => {
				if (result.auth_profile?.slug) {
					setSelectedAuthProfileSlug(result.auth_profile.slug);
				}
				setShowNewBrowserProfileForm(false);
				setNewBrowserProfileName("");
				setNewBrowserProfileSlug("");
			},
		});
	}, [
		activeMethod,
		handleCreateProfile,
		newBrowserProfileName,
		newBrowserProfileSlug,
		selectedConnector?.name,
	]);

	const verifyBrowserAccount = async (connectionId: number) => {
		const result = await verifyBrowser.mutateAsync({
			connection_id: connectionId,
			operation_key: BROWSER_VERIFY_OPERATION,
			input: {},
		});
		if (result.status !== "completed") {
			throw new Error(
				result.error_message ??
					result.message ??
					"Finish sign-in or approve the account check in Lobu, then try again.",
			);
		}
		toast.success(
			liveBrowser ? `${selectedConnector?.name} connected` : "Browser verified",
			{ duration: Infinity, closeButton: true },
		);
	};

	const handleSave = async () => {
		if (!selectedConnector) return;
		if (!configDraft.canSubmit) {
			setSaveError("Reload the latest connection values before saving.");
			return;
		}

		setIsSaving(true);
		setSaveError(null);

		try {
			if (pendingBrowserConnectionId) {
				await verifyBrowserAccount(pendingBrowserConnectionId);
				if (onCreated)
					onCreated(pendingBrowserConnectionId, selectedConnector.key);
				else handleOpenChange(false);
				return;
			}
			const config = cleanConnectorValues(configValues);
			let authProfileSlug: string | null | undefined;
			let appAuthProfileSlug: string | null | undefined;

			if (activeMethod?.type === "env_keys") {
				authProfileSlug = selectedAuthProfileSlug?.trim() || undefined;
				if (!authProfileSlug) {
					throw new Error(
						"Select or create an auth profile before creating the connection.",
					);
				}
			}

			if (activeMethod?.type === "oauth") {
				authProfileSlug = selectedAuthProfileSlug?.trim() || undefined;
				appAuthProfileSlug = selectedAppAuthProfileSlug?.trim() || undefined;

				if (!appAuthProfileSlug) {
					throw new Error(
						`Select or create an ${(activeMethod as OAuthMethod).provider} app profile first.`,
					);
				}
				if (!authProfileSlug) {
					throw new Error(
						`Select or create an ${(activeMethod as OAuthMethod).provider} account profile first.`,
					);
				}
			}

			if (activeMethod?.type === "browser" && !liveBrowser) {
				authProfileSlug = selectedAuthProfileSlug?.trim() || undefined;
				if (!authProfileSlug) {
					throw new Error(
						"Select or create a browser auth profile before creating the connection.",
					);
				}
			}

			if (requiredCapability && !selectedDeviceWorkerId) {
				throw new Error(
					deviceOptions.length === 0
						? browserRequired
							? `${BROWSER_SETUP_HINT} Then refresh.`
							: `No device with the "${requiredCapability}" permission. Open the Lobu app on a device and grant it, then refresh.`
						: "Choose a device to run this connector on.",
				);
			}

			if (isEditMode) {
				const currentDeviceId = editingConnection.device_worker_id ?? undefined;
				await updateConnection.mutateAsync({
					connection_id: editingConnection.id,
					display_name: displayName || undefined,
					auth_profile_slug:
						activeMethod?.type === "oauth" ||
						activeMethod?.type === "env_keys" ||
						activeMethod?.type === "browser"
							? (authProfileSlug ?? undefined)
							: activeMethod?.type === "none"
								? null
								: undefined,
					app_auth_profile_slug:
						activeMethod?.type === "oauth"
							? (appAuthProfileSlug ?? undefined)
							: activeMethod?.type === "none"
								? null
								: undefined,
					config: Object.keys(config).length > 0 ? config : undefined,
					device_worker_id:
						selectedDeviceWorkerId === currentDeviceId &&
						!(liveBrowser && !editingConnection.auth_profile_slug)
							? undefined
							: (selectedDeviceWorkerId ?? null),
				});
				if (liveBrowser) await verifyBrowserAccount(editingConnection.id);
				handleOpenChange(false);
			} else {
				const result = await createConnection.mutateAsync({
					connector_key: selectedConnector.key,
					display_name: displayName || undefined,
					auth_profile_slug:
						activeMethod?.type === "none" ? null : authProfileSlug,
					app_auth_profile_slug:
						activeMethod?.type === "none" ? null : appAuthProfileSlug,
					config: Object.keys(config).length > 0 ? config : undefined,
					created_by: liveBrowser ? undefined : selectedMemberUserId,
					device_worker_id: selectedDeviceWorkerId ?? null,
				});
				if (result.auth_run_id) {
					setPendingAuthRunId(result.auth_run_id);
					setPendingAuthConnectionId(
						result.connection_id ?? result.connection.id,
					);
				} else if (
					result.status === "setup_required" &&
					result.setup_family === "browser"
				) {
					setPendingBrowserConnectionId(result.connection_id ?? null);
					setSaveError(
						result.instructions ??
							"Finish browser setup, then verify the connection.",
					);
				} else {
					if (browserRequired && result.connection.status === "active") {
						toast.success(
							liveBrowser
								? `${selectedConnector.name} connected`
								: "Browser verified",
							{ duration: Infinity, closeButton: true },
						);
					}
					if (onCreated) onCreated(result.connection.id, selectedConnector.key);
					else handleOpenChange(false);
				}
			}
		} catch (error) {
			setSaveError(
				error instanceof Error
					? error.message
					: isEditMode
						? "Failed to update connection"
						: "Failed to create connection",
			);
		} finally {
			setIsSaving(false);
		}
	};

	const handleMethodChange = (index: number) => {
		if (!isEditMode) setSelectedDeviceWorkerId(undefined);
		setSelectedAuthMethodIndex(index);
		setSelectedAuthProfileSlug(undefined);
		setSelectedAppAuthProfileSlug(undefined);
		setShowNewRuntimeProfileForm(false);
		setShowNewAppProfileForm(false);
		setShowNewOAuthAccountForm(false);
		setShowNewBrowserProfileForm(false);
		setSelectedOAuthOptionalScopes([]);
	};

	const handleDeleteAuthProfile = async (
		slug: string,
		kind: "env" | "app" | "account" | "browser",
	) => {
		try {
			await deleteAuthProfileMutation.mutateAsync(slug);
			setConfirmingDeleteProfileSlug(null);
			if (kind === "app") {
				setSelectedAppAuthProfileSlug("");
			} else {
				setSelectedAuthProfileSlug("");
			}
		} catch (error) {
			setSaveError(
				error instanceof Error
					? error.message
					: `Failed to delete ${kind === "app" ? "app" : "auth"} profile`,
			);
		}
	};

	const handleReconnect = async (profileSlug: string) => {
		setSaveError(null);
		const profile = authProfiles.find((item) => item.slug === profileSlug);
		// Only a new, not-yet-authorized account uses the form's app choice.
		// The server resolves the issuing app for an established grant.
		const needsAppSelection =
			profile?.status === "pending_auth" && !profile.granted_scopes?.length;
		await authorization.begin(() =>
			reconnectAuthProfile.mutateAsync({
				auth_profile_slug: profileSlug,
				app_auth_profile_slug: needsAppSelection
					? selectedAppAuthProfileSlug
					: undefined,
				requested_scopes: buildRequestedOAuthScopes(
					activeMethod?.type === "oauth" ? activeMethod : null,
					selectedOAuthOptionalScopes,
				),
			}),
		);
	};

	return {
		pendingBrowserConnectionId,
		configDraft,
		runtimeDraft,
		appDraft,
		// Mutations
		createAuthProfile,
		deleteAuthProfileMutation,
		reconnectAuthProfile,
		deleteConnection,
		installConnectorMutation,

		// Connector definitions
		connectorDefs: connectorCatalog,
		isLoadingDefs,
		isEditMode,

		// Confirmation states
		showDeleteConfirm,
		setShowDeleteConfirm,
		confirmingDeleteProfileSlug,
		setConfirmingDeleteProfileSlug,

		// Install form
		showInstallForm,
		setShowInstallForm,
		installSourceUrl,
		setInstallSourceUrl,
		installError,
		setInstallError,
		installingConnectorKey,
		searchQuery,
		setSearchQuery,

		// Preview
		previewConnector,
		handleInstallPreviewedConnector,
		clearPreview,

		// Selection state
		selectedConnector,
		displayName,
		setDisplayName,
		configValues,
		selectedAuthMethodIndex,
		selectedAuthProfileSlug,
		setSelectedAuthProfileSlug,
		selectedAppAuthProfileSlug,
		setSelectedAppAuthProfileSlug,
		showNewRuntimeProfileForm,
		setShowNewRuntimeProfileForm,
		newRuntimeProfileName,
		setNewRuntimeProfileName,
		newRuntimeProfileSlug,
		setNewRuntimeProfileSlug,
		newRuntimeProfileValues,
		setNewRuntimeProfileValues,
		showNewAppProfileForm,
		setShowNewAppProfileForm,
		newAppProfileName,
		setNewAppProfileName,
		newAppProfileSlug,
		setNewAppProfileSlug,
		newAppProfileValues,
		setNewAppProfileValues,
		showNewOAuthAccountForm,
		setShowNewOAuthAccountForm,
		newOAuthAccountName,
		setNewOAuthAccountName,
		newOAuthAccountSlug,
		setNewOAuthAccountSlug,
		showNewBrowserProfileForm,
		setShowNewBrowserProfileForm,
		newBrowserProfileName,
		setNewBrowserProfileName,
		newBrowserProfileSlug,
		setNewBrowserProfileSlug,
		isSaving,
		saveError: saveError ?? authorization.error,
		authorizationUrl: authorization.continueUrl,
		setSaveError,
		handleAuthClose,
		pendingAuthRunId,
		setPendingAuthRunId,

		// Auth profiles
		authProfiles,

		// Derived auth state
		selectableMethods,
		activeMethod,
		runtimeEnvProfiles,
		runtimeOAuthProfiles,
		appOAuthProfiles,
		browserProfiles,
		runtimeProfileSchema,
		appProfileSchema,
		availableOAuthOptionalScopes,
		selectedOAuthOptionalScopes,
		setSelectedOAuthOptionalScopes,

		// Computed
		hasUniqueOptionsFields,
		connections,
		isNoAuthConnector,
		existingNoAuthConnection,

		// Member picker (admin only)
		selectedMemberUserId,
		setSelectedMemberUserId,
		isAdmin,

		// Device-worker binding ("Run on")
		devices,
		deviceOptions,
		lockedDeviceWorkerIdFromProfile,
		browserRequired,
		browserSetupHint: BROWSER_SETUP_HINT,
		liveBrowser,
		requiredCapability,
		selectedDeviceWorkerId,
		setSelectedDeviceWorkerId,
		refreshDevices: refetchDevices,
		devicesRefreshing: devicesFetching,

		// Handlers
		handleOpenChange,
		handleConnectorSelect,
		handleBack,
		handleValuesChange: setConfigValues,
		normalizeStringValues,
		navigateOAuthPopup,
		handleCreateRuntimeProfile,
		handleCreateAppProfile,
		handleCreateOAuthAccountProfile,
		handleCreateBrowserProfile,
		handleSave,
		handleMethodChange,
		handleDeleteAuthProfile,
		handleReconnect,
	};
}
